import React, { useState, useEffect } from "react";
import axios from "axios";

const Step1BusinessInfo = ({ formData, updateFormData, nextStep, prevStep, token }) => {
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);

  // Fetch business categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}get-categories`, {
          headers: { "x-access-token": token },
        });
        if (response.data.status === 200) {
          setCategories(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [token]);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}get-country`, {
          headers: { "x-access-token": token },
        });
        if (response.data.status === 200) {
          setCountries(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, [token]);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.country_id) {
        setStates([]);
        return;
      }

      setLoadingStates(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}get-state`,
          { country_id: formData.country_id },
          { headers: { "x-access-token": token } }
        );
        if (response.data.status === 200) {
          setStates(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, [formData.country_id, token]);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Check if states are available for the selected country
  const hasStatesAvailable = states.length > 0;

  const validateAndNext = () => {
    const newErrors = {};

    if (!formData.corporation_legal_name?.trim()) {
      newErrors.corporation_legal_name = "Company name is required";
    }

    if (!formData.business_category) {
      newErrors.business_category = "Business category is required";
    }

    if (!formData.city?.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.country_id) {
      newErrors.country_id = "Country is required";
    }

    // State is only required if states are available for the selected country
    if (hasStatesAvailable && !formData.state_id) {
      newErrors.state_id = "State/Province is required";
    }

    // Optional: validate website URL format if provided
    if (formData.website && formData.website.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
      if (!urlPattern.test(formData.website)) {
        newErrors.website = "Please enter a valid website URL";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      nextStep();
    }
  };

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">Business Information</h3>

        <div className="form-row">
          <div className="form-group">
            <label>
              Company Legal Name <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter company legal name"
              value={formData.corporation_legal_name}
              onChange={(e) => handleInputChange("corporation_legal_name", e.target.value)}
            />
            {errors.corporation_legal_name && (
              <div className="error-message">{errors.corporation_legal_name}</div>
            )}
          </div>

          <div className="form-group">
            <label>
              Business Category <span className="required">*</span>
            </label>
            <select
              value={formData.business_category}
              onChange={(e) => handleInputChange("business_category", e.target.value)}
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.category_name}
                </option>
              ))}
            </select>
            {errors.business_category && (
              <div className="error-message">{errors.business_category}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Website URL</label>
            <input
              type="text"
              placeholder="https://www.example.com"
              value={formData.website}
              onChange={(e) => handleInputChange("website", e.target.value)}
            />
            {errors.website && <div className="error-message">{errors.website}</div>}
            <div className="help-text">Optional - Enter the company website if available</div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              Country <span className="required">*</span>
            </label>
            <select
              value={formData.country_id}
              onChange={(e) => {
                handleInputChange("country_id", e.target.value);
                handleInputChange("state_id", ""); // Reset state when country changes
              }}
            >
              <option value="">Select a country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.country_name}
                </option>
              ))}
            </select>
            {errors.country_id && <div className="error-message">{errors.country_id}</div>}
          </div>

          <div className="form-group">
            <label>
              State/Province {hasStatesAvailable && <span className="required">*</span>}
            </label>
            <select
              value={formData.state_id}
              onChange={(e) => handleInputChange("state_id", e.target.value)}
              disabled={!formData.country_id || loadingStates || (!hasStatesAvailable && !loadingStates)}
            >
              <option value="">
                {loadingStates
                  ? "Loading..."
                  : !formData.country_id
                  ? "Select a country first"
                  : hasStatesAvailable
                  ? "Select a state/province"
                  : "Not available for this country"}
              </option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.state_name}
                </option>
              ))}
            </select>
            {errors.state_id && <div className="error-message">{errors.state_id}</div>}
            {formData.country_id && !loadingStates && !hasStatesAvailable && (
              <div className="help-text">State/Province is optional for this country</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              City <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter city"
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
            />
            {errors.city && <div className="error-message">{errors.city}</div>}
          </div>
        </div>
      </div>

      <div className="step-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          Back
        </button>
        <button className="btn btn-primary" onClick={validateAndNext}>
          Next: Campaign Details
        </button>
      </div>
    </div>
  );
};

export default Step1BusinessInfo;
